import { ReactRenderer } from '@tiptap/react'
import tippy, { type Instance as TippyInstance } from 'tippy.js'
import { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion'
import type { Editor } from '@tiptap/core'
import MemeSuggestionList from './MemeSuggestionList'
import { MemeAutocompleteItem, getGlobalMemes, rankMemeMatches } from './memeAutocompleteSource'
import type { EditorState } from '@tiptap/pm/state'
import { rangeIsInsideCodeLiteral } from './codeLiteralContext'

const MAX_QUERY_LENGTH = 50

// Suggestion config for the `[[` meme trigger. Global meme labels come from
// the flag-gated /api/v1/memes/autocomplete endpoint (module-cached), and the
// selected item is inserted as plain `[[text]]` text — MemeExtension's
// post-processor immediately converts it into the marked meme link, keeping a
// single source of truth for the mark shape.
export const createMemeSuggestion = () => {
    return {
        items: async ({ query }: { query: string }): Promise<MemeAutocompleteItem[]> => {
            // `]` means the tag is already closed and newlines mean the match ran
            // away — the dropdown should get out of the way, not "help".
            if (query.includes(']') || query.includes('\n') || query.length > MAX_QUERY_LENGTH) {
                return []
            }

            const memes = await getGlobalMemes()
            return rankMemeMatches(memes, query)
        },

        render: () => {
            let component: ReactRenderer
            let popup: TippyInstance[] | undefined

            const syncPopupVisibility = (items: MemeAutocompleteItem[]) => {
                if (items.length) {
                    popup?.[0]?.show()
                } else {
                    popup?.[0]?.hide()
                }
            }

            return {
                onStart: (props: SuggestionProps<MemeAutocompleteItem>) => {
                    component = new ReactRenderer(MemeSuggestionList, {
                        props,
                        editor: props.editor,
                    })
                    component.element.setAttribute(
                        'data-suggestion-editor',
                        props.editor.instanceId,
                    )

                    if (!props.clientRect) return

                    popup = tippy('body', {
                        getReferenceClientRect: props.clientRect,
                        appendTo: () => document.body,
                        content: component.element,
                        showOnCreate: false,
                        interactive: true,
                        trigger: 'manual',
                        placement: 'bottom-start',
                        zIndex: 10000,
                    })

                    syncPopupVisibility(props.items || [])
                },

                onUpdate(props: SuggestionProps<MemeAutocompleteItem>) {
                    component?.updateProps(props)

                    if (!props.clientRect) return

                    popup?.[0]?.setProps({
                        getReferenceClientRect: props.clientRect,
                    })

                    syncPopupVisibility(props.items || [])
                },

                onKeyDown(props: SuggestionKeyDownProps) {
                    const event = props.event as KeyboardEvent

                    if (event.key === 'Escape') {
                        popup?.[0]?.hide()
                        return true
                    }

                    return (
                        (
                            component?.ref as {
                                onKeyDown?: (props: SuggestionKeyDownProps) => boolean
                            }
                        )?.onKeyDown?.(props) || false
                    )
                },

                onExit() {
                    popup?.[0]?.destroy()
                    component?.destroy()
                },
            }
        },

        char: '[[',
        allowSpaces: true,
        // Memes can start anywhere (the server-side MEME_REGEX has no prefix
        // requirement), so don't restrict what precedes `[[`.
        allowedPrefixes: null,
        startOfLine: false,
        allow: ({ state, range }: { state: EditorState; range: { from: number; to: number } }) =>
            !rangeIsInsideCodeLiteral(state, range),

        command: ({
            editor,
            range,
            props,
        }: {
            editor: Editor
            range: { from: number; to: number }
            props: MemeAutocompleteItem
        }) => {
            window.logEv?.('SELECT_MEME_AUTOCOMPLETE', { meme_text: props.text })
            editor
                .chain()
                .focus()
                .deleteRange(range)
                .insertContent([
                    { type: 'text', text: `[[${props.text}]]` },
                    { type: 'text', text: ' ' },
                ])
                .run()
        },
    }
}
